import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import ExerciseWorksheet from "@/components/ExerciseWorksheet";
import CopyForNotesButton from "@/components/CopyForNotesButton";
import AdminHomeButton from "@/components/AdminHomeButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { resolvePlanPrescription } from "@/lib/patientPrescription";

const localDate = () => {
  const today = new Date();
  return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
};
const prescriptionFields = ["exercise_id", "instructions", "sets", "reps", "hold_time", "frequency", "band_colour", "resistance", "equipment", "key_cues", "clinician_notes"];

export default function ExerciseProgramSharing() {
  const { user } = useAuth();
  const { programId } = useParams();
  const [query] = useSearchParams();
  const [program, setProgram] = useState(null);
  const [preview, setPreview] = useState(Boolean(programId || query.get("plan")));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const printedAutomatically = useRef(false);
  useEffect(() => {
    document.title = "Exercise worksheet | Bexley Road Physiotherapy";
    let active = true;
    const load = async () => {
      if (programId) return (await api.get(`/admin/exercise-programs/${programId}`)).data;
      if (query.get("copy")) {
        const { data } = await api.get(`/admin/exercise-programs/${encodeURIComponent(query.get("copy"))}`);
        return {
          patient_user_id: data.patient_user_id || null, patient_name: data.patient_name, patient_email: data.patient_email || "", clinician_name: data.clinician_name,
          worksheet_date: localDate(), review_date: data.review_date || "", message: data.message, notes: data.notes, exercises: data.exercises,
        };
      }
      if (query.get("plan")) {
        const patientId = query.get("patient");
        if (!patientId) throw new Error("Select a patient before opening a plan handout.");
        const [planResponse, patientResponse] = await Promise.all([
          api.get(`/admin/plans/${encodeURIComponent(query.get("plan"))}`),
          api.get(`/admin/user/${encodeURIComponent(patientId)}`),
        ]);
        const plan = planResponse.data;
        const patient = patientResponse.data.user;
        const exercises = (plan.exercises || []).filter((row) => row.is_active).map(resolvePlanPrescription);
        if (!exercises.length) throw new Error("This plan has no current exercises to share.");
        return {
          patient_user_id: patient.id,
          patient_name: patient.name || "",
          patient_email: patient.email || "",
          clinician_name: user.display_name || user.name || "",
          worksheet_date: localDate(),
          review_date: plan.review_date || "",
          message: "Please follow your exercise program using the secure link below.",
          notes: "",
          exercises,
        };
      }
      let rows;
      if (query.get("playlist")) {
        const { data } = await api.get(`/admin/playlists/${encodeURIComponent(query.get("playlist"))}`);
        if (data.is_active === false) throw new Error("This playlist is archived. Select an active playlist.");
        rows = data.exercises.map((row) => ({ ...row.exercise, clinician_notes: row.notes || "" }));
      } else {
        const ids = query.getAll("exercise");
        const { data } = await api.get("/admin/exercise-manager/exercises");
        rows = ids.map((id) => data.find((exercise) => exercise.id === id));
      }
      if (!rows.length || rows.some((row) => !row?.id)) throw new Error("Select available exercises or a playlist from the library first.");
      if (rows.length > 100) throw new Error("Select up to 100 exercises per worksheet.");
      if (rows.some((row) => row.is_active === false)) throw new Error("This selection includes archived exercises. Select active exercises.");
      const selectedPatient = query.get("patient")
        ? (await api.get(`/admin/user/${encodeURIComponent(query.get("patient"))}`)).data.user
        : null;
      return {
        patient_user_id: selectedPatient?.id || null,
        patient_name: selectedPatient?.name || "", patient_email: selectedPatient?.email || "", clinician_name: user.display_name || user.name || "", worksheet_date: localDate(),
        review_date: "",
        message: "Please follow your exercise program using the secure link below.", notes: "",
        exercises: rows.map((row) => ({ ...row, exercise_id: row.id, instructions: row.instructions || row.description || "", key_cues: "", clinician_notes: row.clinician_notes || "" })),
      };
    };
    load().then((data) => { if (active) setProgram(data); }).catch((err) => {
      if (active) setError(err.response?.data?.detail ? formatApiError(err.response.data.detail) : err.message);
    });
    return () => { active = false; };
  }, [programId, query, user]);

  useEffect(() => {
    if (program && preview && query.get("mode") === "print" && !printedAutomatically.current) {
      printedAutomatically.current = true;
      window.setTimeout(() => window.print(), 0);
    }
  }, [preview, program, query]);

  const change = (key, value) => { setProgram((current) => ({ ...current, [key]: value })); setError(""); };
  const changeExercise = (index, key, value) => setProgram((current) => ({ ...current, exercises: current.exercises.map((row, i) => i === index ? { ...row, [key]: value } : row) }));
  const save = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        patient_user_id: program.patient_user_id || null,
        patient_name: program.patient_name, patient_email: program.patient_email || null,
        clinician_name: program.clinician_name, worksheet_date: program.worksheet_date,
        review_date: program.review_date || null,
        message: program.message, notes: program.notes,
        exercises: program.exercises.map((row) => Object.fromEntries(prescriptionFields.map((key) => [key, String(row[key] || "")]))),
      };
      const { data } = await api.post("/admin/exercise-programs", payload);
      window.location.assign(`/admin/exercise-programs/${data.id}`);
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Unable to save worksheet."); }
    finally { setBusy(false); }
  };
  const send = async () => {
    setBusy(true); setError(""); setNotice("");
    try {
      const { data } = await api.post(`/admin/exercise-programs/${program.id}/send`);
      setProgram((current) => ({ ...current, ...data }));
      setNotice("Email accepted by the mail service. Delivery to the inbox is not yet confirmed.");
    } catch (err) { setError(err.response?.data?.detail ? formatApiError(err.response.data.detail) : "Unable to confirm email status. Refresh this page before retrying."); }
    finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true); setError("");
    try {
      await api.post(`/admin/exercise-programs/${program.id}/revoke`);
      setProgram((current) => ({ ...current, revoked: true }));
      setNotice("Patient link revoked.");
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return <main className="program-page mx-auto max-w-4xl p-4 sm:p-8">
    <div className="program-controls mb-5 space-y-4">
      <div className="flex flex-wrap items-center gap-3"><a className="text-sm text-teal-700 underline" href="/admin/exercises">Back to Exercise Library</a><AdminHomeButton /></div>
      <h1 className="text-2xl font-bold font-display text-slate-900">Exercise worksheet &amp; sharing</h1>
      <p className="text-sm text-slate-600">Review the prescription and patient details, then preview, print or send. Saving creates a fixed copy of this program.</p>
      {error && <p role="alert" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
      {notice && <p role="status" className="rounded-xl bg-teal-50 p-3 text-sm text-teal-800">{notice}</p>}
      {!program && !error && <p>Loading exercises…</p>}
      {program && <>
        {!program.id && !preview && <form onSubmit={(event) => { event.preventDefault(); setPreview(true); }} className="space-y-4">
          <div className="grid gap-4 rounded-2xl border bg-white p-4 sm:grid-cols-2">
            <Field label="Patient name"><Input required maxLength={150} value={program.patient_name} onChange={(e) => change("patient_name", e.target.value)} /></Field>
            <Field label="Patient email address (required to email)"><Input type="email" maxLength={254} value={program.patient_email} onChange={(e) => change("patient_email", e.target.value)} /></Field>
            <Field label="Clinician name"><Input required maxLength={150} value={program.clinician_name} onChange={(e) => change("clinician_name", e.target.value)} /></Field>
            <Field label="Date"><Input type="date" required value={program.worksheet_date} onChange={(e) => change("worksheet_date", e.target.value)} /></Field>
            <Field label="Review date (optional)"><Input type="date" value={program.review_date || ""} onChange={(e) => change("review_date", e.target.value)} /></Field>
            <Field label="Short email message"><Textarea maxLength={300} value={program.message} onChange={(e) => change("message", e.target.value)} /><span className="text-xs text-slate-500">Keep this message general. Put clinical information in program notes below; do not include it in email.</span></Field>
            <Field label="Program notes (shown to the patient)"><Textarea maxLength={4000} value={program.notes} onChange={(e) => change("notes", e.target.value)} /></Field>
          </div>
          {program.exercises.map((row, index) => <section key={index} className="space-y-3 rounded-2xl border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2"><h2 className="font-semibold">{index + 1}. {row.title}</h2><CopyForNotesButton prescription={row} compact /></div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">{[["Sets", "sets"], ["Reps", "reps"], ["Hold (include units)", "hold_time"], ["Frequency", "frequency"], ["Band / resistance", "band_colour"], ["Equipment", "equipment"]].map(([label, key]) => <Field key={key} label={label}><Input maxLength={key === "frequency" || key === "equipment" ? 200 : 100} value={row[key] || ""} onChange={(e) => changeExercise(index, key, e.target.value)} /></Field>)}</div>
            <Field label="Instructions"><Textarea maxLength={4000} value={row.instructions} onChange={(e) => changeExercise(index, "instructions", e.target.value)} /></Field>
            <Field label="Key cues"><Textarea maxLength={2000} value={row.key_cues} onChange={(e) => changeExercise(index, "key_cues", e.target.value)} /></Field>
            <Field label="Clinician notes (shown to the patient)"><Textarea maxLength={4000} value={row.clinician_notes} onChange={(e) => changeExercise(index, "clinician_notes", e.target.value)} /></Field>
          </section>)}
          <Button type="submit">Preview Worksheet</Button>
        </form>}
        {(preview || program.id) && <>
          <div className="flex flex-wrap gap-2">
            {!program.id && <Button variant="outline" onClick={() => setPreview(false)}>Edit details</Button>}
            {program.id && <Button variant="outline" onClick={() => window.location.assign(`/admin/exercise-programs/new?copy=${program.id}`)}>Edit as New Worksheet</Button>}
            <Button variant="outline" onClick={() => window.print()}>Print Worksheet</Button>
            <CopyForNotesButton prescriptions={program.exercises} />
            {!program.id && <Button disabled={busy} onClick={save}>{busy ? "Saving…" : "Save Worksheet & Continue"}</Button>}
            {program.id && <Button disabled={busy || !program.patient_email || program.revoked || ["sent", "sending"].includes(program.email_status)} onClick={send}>{busy ? "Working…" : program.email_status === "sent" ? "Email Sent" : "Send to Patient"}</Button>}
            {program.id && !program.revoked && program.email_status === "sent" && <Button variant="destructive" disabled={busy} onClick={revoke}>Revoke Patient Link</Button>}
          </div>
          <p className="text-sm text-slate-500">To save a PDF, choose “Save as PDF” in the print dialog. Video links remain available on the worksheet.</p>
          {program.id && <div className="rounded-xl border bg-white p-4 text-sm space-y-2">
            <p><strong>Email to:</strong> {program.patient_email || "No email address provided (print-only worksheet)."}</p>
            <p className="whitespace-pre-wrap"><strong>Message:</strong> {program.message}</p>
            <p>The email includes patient and clinician names, this message, and a private link that expires 30 days after sending. Anyone with the link can view the program.</p>
            <p>Bookmark this page to return to the saved worksheet or revoke its patient link.</p>
            <p>Editing creates a new worksheet. Revoke the previous link if its prescription is no longer appropriate.</p>
            {program.expires_at && <p>Link expires: {new Date(program.expires_at).toLocaleDateString()}</p>}
            {program.revoked && <p className="font-semibold">Patient link revoked.</p>}
            {program.email_status === "sending" && <p>Email is being processed. Refresh to check its status.</p>}
          </div>}
        </>}
      </>}
    </div>
    {program && (preview || program.id) && <ExerciseWorksheet program={program} showNoteCopy />}
  </main>;
}

function Field({ label, children }) {
  return <label className="block space-y-1 text-sm font-medium text-slate-700"><span>{label}</span>{children}</label>;
}
